<template>
  <div>
    <h2>Email address</h2>
    <p>
      <span>Working on quote&#160;</span>
      <router-link :to="{ name: 'quote', params: { id: $route.params.id } }">{{
        $route.params.id
      }}</router-link>
    </p>
    <v-alert :value="!$route.params.emailId" type="info"
      >Enter the Address (email address) and the Personal (name) into the boxes
      below.</v-alert
    >
    <v-alert :value="$route.params.emailId" type="warning"
      >Editing the name of this person will change their name on other quotes
      they have made. So use this feature to smarten up the name not to change
      it. The email address cannot be changed here. Add a new one on the
      quote.</v-alert
    >
    <v-card>
      <v-card-text>
        <h2>Information</h2>
        <v-form ref="quoteEmailForm" v-model="valid" lazy-validation>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="address"
                label="Address"
                outline
                v-on="on"
                :readonly="email != undefined"
                :rules="addressRules"
              />
            </template>
            <span>Email address</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="personal"
                label="Personal"
                outline
                v-on="on"
                :rules="personalRules"
              />
            </template>
            <span>Name identifying the person</span>
          </v-tooltip>
          <v-tooltip bottom v-if="email">
            <template v-slot:activator="{ on }">
              <v-checkbox v-model="archived" label="Archived" v-on="on" />
            </template>
            <span>Archive this email (affects this quote only)</span>
          </v-tooltip>
          <div>
            <v-btn @click="save" color="info">
              <v-icon>save</v-icon>Save
            </v-btn>
            <v-btn :to="{ name: 'quote', params: { id: $route.params.id } }">
              <v-icon>undo</v-icon>Cancel
            </v-btn>
          </div>
        </v-form>
        <div v-if="entity">
          Linked to entity&#160;
          <EntityZ :entity="entity" format="system" />
        </div>
      </v-card-text>
    </v-card>
    <v-card v-if="$route.params.emailId">
      <v-card-text>
        <h2>Related quotes</h2>
        <table border="1">
          <tr>
            <th>Id</th>
            <th>Name</th>
            <th>Total</th>
            <th>Archived</th>
          </tr>
          <tr v-for="quoteEmail in modifiedQuoteEmails" :key="quoteEmail.id">
            <td>
              <router-link
                :to="{ name: 'quote', params: { id: quoteEmail.quoteid } }"
                >{{ quoteEmail.quoteid }}</router-link
              >
            </td>
            <td>
              <span v-if="quoteEmail.quote">{{ quoteEmail.quote.name }}</span>
            </td>
            <td>
              <span v-if="quoteEmail.quote">{{
                quoteEmail.quote.total | toCurrency
              }}</span>
            </td>
            <td>{{ quoteEmail.archived }}</td>
          </tr>
        </table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import EntityZ from "@/components/link/EntityZ";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "QuoteEmail",
  components: { EntityZ },
  computed: {
    modifiedQuoteEmails() {
      if (!this.quoteEmails) {
        return undefined;
      }
      return this.quoteEmails.map((qe) => {
        const quote = this.quotes ? this.quotes[qe.quoteid] : undefined;
        return {
          ...qe,
          quote
        };
      });
    }
  },
  data() {
    return {
      quoteEmail: null,
      email: null,
      quoteEmails: null,
      quotes: null,
      entity: null,
      address: null,
      personal: null,
      archived: null,
      valid: false,
      personalRules: [(v) => !!v || "Personal is required"],
      addressRules: [
        (v) => {
          if (!v) {
            return "Address is required";
          }
          if (v.length > 0) {
            const pattern =
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return pattern.test(v) || "Invalid e-mail.";
          }
        }
      ]
    };
  },
  methods: {
    async fetchQuoteEmail() {
      const response = await nbmlaw.get(
        `/cors/admin/sales/quotes/${this.$route.params.id}/emails/${this.$route.params.emailId}`
      );
      // console.log(response.data);
      this.quoteEmail = response.data;
      if (this.quoteEmail) {
        this.archived = this.quoteEmail.archived;
      }
      this.fetchEntity();
    },
    async fetchEmail() {
      const response = await nbmlaw.get(
        `/cors/admin/sales/emails/${this.$route.params.emailId}`
      );
      // console.log(response.data);
      this.email = response.data;
      if (this.email) {
        this.address = this.email.address;
        this.personal = this.email.personal;
      }
      this.fetchEntity();
    },
    async fetchQuoteEmails() {
      const response = await nbmlaw.get(
        `/cors/admin/sales/emails/${this.$route.params.emailId}/quoteEmails`
      );
      // console.log(response.data);
      this.quoteEmails = response.data;
    },
    async fetchQuotes() {
      const response = await nbmlaw.get(
        `/cors/admin/sales/emails/${this.$route.params.emailId}/quotes`
      );
      // console.log(response.data);
      this.quotes = response.data;
    },
    async fetchEntity() {
      this.entity = null;
      if (!this.email.entityid) {
        return;
      }
      const response = await nbmlaw.get(
        `/cors/admin/directory/entities/${this.email.entityid}`
      );
      // console.log(response.data);
      this.entity = response.data;
    },
    async save() {
      if (this.$refs.quoteEmailForm.validate()) {
        if (this.$route.params.emailId) {
          await nbmlaw.put(
            `/cors/admin/sales/quotes/${this.$route.params.id}/emails/${this.$route.params.emailId}/${this.archived}`,
            { name: this.personal }
          );
          // console.log(response.data);
          this.$router.push({
            name: "quote",
            params: { id: this.$route.params.id }
          });
        } else {
          await nbmlaw.post(
            `/cors/admin/sales/quotes/${this.$route.params.id}/emails`,
            {
              name: this.personal,
              value: this.address
            }
          );
          // console.log(response.data);
          this.$router.push({
            name: "quote",
            params: { id: this.$route.params.id }
          });
        }
      }
    }
  },
  created() {
    if (this.$route.params.emailId) {
      this.fetchQuoteEmail();
      this.fetchEmail();
      this.fetchQuoteEmails();
      this.fetchQuotes();
    }
  }
};
</script>